import { type JSX, ErrorBoundary, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import type { ApexOptions } from "apexcharts";
import { ApexChart } from "./apex";

/**
 * Apex charts — the production charting layer, ported from the c9erp-web design
 * system (`src/core/ui/charts/index.tsx`) so the admin's charts look identical.
 *
 * Every colour — series, grid, axis text, tooltip — is resolved from our CSS
 * tokens and RE-THEMED LIVE when `[data-theme]` flips (a MutationObserver
 * re-reads the vars). Each chart reserves its height so there is no layout shift
 * while the Apex chunk loads.
 *
 * ⚠️ THE NON-OBVIOUS BIT: ApexCharts applies colours as **SVG attributes**, where
 * `var(--token)` does NOT resolve. Token refs must be resolved to a computed hex
 * before they reach Apex — otherwise the series silently fall back to Apex's own
 * default palette and the charts stop matching the design system.
 */

/* Hardcoded hex mirror of the series tokens (styles/tokens.css). Used ONLY as a
   last-resort fallback: `getComputedStyle` returns "" for a var it can't resolve
   yet — e.g. a chart mounting mid-navigation before styles apply, or during a
   theme flip — and an empty colour makes ApexCharts throw while drawing. We never
   hand Apex an empty string. */
const FALLBACK_SERIES = ["#1868DB", "#22A06B", "#BF63F3", "#E06C00", "#2898BD", "#DA62AC", "#5B7F24"];
const FALLBACK_GRID = "#E4E5E8";
const FALLBACK_AXIS = "#6B6E76";
const FALLBACK_TEXT = "#1A1A1A";
const FALLBACK_FONT = "Inter, system-ui, sans-serif";

function readVar(name: string, fallback = ""): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Resolve a `var(--token)` ref to its computed hex; plain hex passes through. */
function resolve(color: string, fallback = FALLBACK_SERIES[0]!): string {
  const m = /^var\((--[\w-]+)\)$/.exec(color.trim());
  const resolved = m ? readVar(m[1]!) : color.trim();
  return resolved || fallback;
}
function resolveColors(colors?: string[]): string[] | undefined {
  return colors?.map((c, i) => resolve(c, FALLBACK_SERIES[i % FALLBACK_SERIES.length]!));
}

interface ChartTheme {
  series: string[];
  grid: string;
  axis: string;
  font: string;
  mode: "light" | "dark";
}

function useChartTheme() {
  const read = (): ChartTheme => ({
    series: [
      readVar("--c9-blue-700", FALLBACK_SERIES[0]!),
      readVar("--c9-green-600", FALLBACK_SERIES[1]!),
      readVar("--c9-purple-500", FALLBACK_SERIES[2]!),
      readVar("--c9-orange-600", FALLBACK_SERIES[3]!),
      readVar("--c9-teal-600", FALLBACK_SERIES[4]!),
      readVar("--c9-magenta-500", FALLBACK_SERIES[5]!),
      readVar("--c9-lime-700", FALLBACK_SERIES[6]!),
    ],
    grid: readVar("--c9-border", FALLBACK_GRID),
    axis: readVar("--c9-text-subtlest", FALLBACK_AXIS),
    font: readVar("--c9-font-family-body", FALLBACK_FONT),
    mode:
      typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme") === "dark"
        ? "dark"
        : "light",
  });

  const [theme, setTheme] = createSignal<ChartTheme>(read());
  onMount(() => {
    setTheme(read());
    const obs = new MutationObserver(() => setTheme(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    onCleanup(() => obs.disconnect());
  });
  return theme;
}

function base(t: ChartTheme, height: number, formatter?: (v: number) => string): ApexOptions {
  return {
    chart: {
      fontFamily: t.font,
      foreColor: t.axis,
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      animations: { enabled: true, speed: 500 },
      parentHeightOffset: 0,
      height,
    },
    colors: t.series,
    grid: {
      borderColor: t.grid,
      strokeDashArray: 4,
      padding: { left: 12, right: 12, top: 0, bottom: 4 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    // NOTE: only set tooltip.y / yaxis.formatter when a formatter exists — an
    // explicit `undefined` key makes ApexCharts throw while processing the config
    // (the chart then never renders at all).
    tooltip: {
      theme: t.mode,
      style: { fontFamily: t.font },
      ...(formatter ? { y: { formatter } } : {}),
    },
    stroke: { curve: "smooth", width: 2, lineCap: "round" },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: t.axis, fontSize: "12px", fontFamily: t.font } },
    },
    yaxis: {
      labels: {
        style: { colors: t.axis, fontSize: "12px", fontFamily: t.font },
        ...(formatter ? { formatter } : {}),
      },
    },
  };
}

/** Show a top-right legend only when there is more than one series. */
function legendFor(t: ChartTheme, count: number): ApexOptions["legend"] {
  return count > 1
    ? {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontFamily: t.font,
        labels: { colors: t.axis },
        markers: { strokeWidth: 0 },
        itemMargin: { horizontal: 8 },
      }
    : { show: false };
}

export type Series = { name: string; data: number[] }[];

/* Inline fallback when a chart throws — occupies the reserved height so isolating
   a broken chart causes no layout shift. Charts are decorative; a bad render must
   degrade to this, never escalate to the app-level crash screen. */
function ChartFallback(props: { height: number }): JSX.Element {
  return (
    <div
      class="flex items-center justify-center text-body-s text-[color:var(--c9-text-subtlest)]"
      style={{ "min-height": `${props.height}px` }}
    >
      Couldn't render chart
    </div>
  );
}

/* Every chart renders inside this: a fixed-height box (reserves space so loading
   the async chunk causes no layout shift) wrapped in an ErrorBoundary, so a
   synchronous throw while ApexCharts processes its config is caught HERE. */
function Shell(props: { height: number; class?: string; children: JSX.Element }): JSX.Element {
  return (
    <div class={props.class} style={{ "min-height": `${props.height}px` }}>
      <ErrorBoundary fallback={<ChartFallback height={props.height} />}>
        {props.children}
      </ErrorBoundary>
    </div>
  );
}

/** Area chart with a soft token-coloured gradient fill. */
export function AreaChart(props: {
  series: Series;
  categories?: (string | number)[];
  height?: number;
  colors?: string[];
  valueFormatter?: (v: number) => string;
  class?: string;
}): JSX.Element {
  const theme = useChartTheme();
  const height = () => props.height ?? 240;
  const options = createMemo<ApexOptions>(() => {
    const b = base(theme(), height(), props.valueFormatter);
    return {
      ...b,
      colors: resolveColors(props.colors) ?? theme().series,
      legend: legendFor(theme(), props.series.length),
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95] },
      },
      xaxis: { ...b.xaxis, categories: props.categories ?? [] },
    };
  });
  return (
    <Shell height={height()} class={props.class}>
      <ApexChart type="area" width="100%" height={height()} options={options()} series={props.series} />
    </Shell>
  );
}

/** Multi-series line chart. */
export function LineChart(props: {
  series: Series;
  categories?: (string | number)[];
  height?: number;
  colors?: string[];
  valueFormatter?: (v: number) => string;
  class?: string;
}): JSX.Element {
  const theme = useChartTheme();
  const height = () => props.height ?? 240;
  const options = createMemo<ApexOptions>(() => {
    const b = base(theme(), height(), props.valueFormatter);
    return {
      ...b,
      colors: resolveColors(props.colors) ?? theme().series,
      legend: legendFor(theme(), props.series.length),
      stroke: { curve: "smooth", width: 3, lineCap: "round" },
      markers: { size: 0, hover: { size: 5 } },
      xaxis: { ...b.xaxis, categories: props.categories ?? [] },
    };
  });
  return (
    <Shell height={height()} class={props.class}>
      <ApexChart type="line" width="100%" height={height()} options={options()} series={props.series} />
    </Shell>
  );
}

/**
 * Vertical (or horizontal) bar chart with rounded caps; optional stacking.
 * `distributed` colours each bar from a distinct ramp slot — for a SINGLE-series
 * categorical chart where the bars ARE the categories, not a trend.
 */
export function ColumnChart(props: {
  series: Series;
  categories?: (string | number)[];
  height?: number;
  horizontal?: boolean;
  stacked?: boolean;
  distributed?: boolean;
  colors?: string[];
  valueFormatter?: (v: number) => string;
  class?: string;
}): JSX.Element {
  const theme = useChartTheme();
  const height = () => props.height ?? 240;
  const options = createMemo<ApexOptions>(() => {
    const b = base(theme(), height(), props.valueFormatter);
    return {
      ...b,
      chart: { ...b.chart, stacked: props.stacked ?? false },
      colors: resolveColors(props.colors) ?? theme().series,
      // A distributed single-series chart has one bar per category, so suppress
      // the (otherwise auto-shown) legend — the axis already labels each bar.
      legend: props.distributed ? { show: false } : legendFor(theme(), props.series.length),
      stroke: { show: false },
      plotOptions: {
        bar: {
          horizontal: props.horizontal ?? false,
          distributed: props.distributed ?? false,
          columnWidth: "55%",
          borderRadius: 4,
          borderRadiusApplication: "end",
        },
      },
      xaxis: { ...b.xaxis, categories: props.categories ?? [] },
    };
  });
  return (
    <Shell height={height()} class={props.class}>
      <ApexChart type="bar" width="100%" height={height()} options={options()} series={props.series} />
    </Shell>
  );
}

/** Donut (or pie) with a centre total and a right-side legend. */
export function PieChart(props: {
  series: number[];
  labels: string[];
  height?: number;
  colors?: string[];
  donut?: boolean;
  totalLabel?: string;
  valueFormatter?: (v: number) => string;
  class?: string;
}): JSX.Element {
  const theme = useChartTheme();
  const height = () => props.height ?? 260;
  const isDonut = () => props.donut !== false;
  const options = createMemo<ApexOptions>(() => {
    const t = theme();
    return {
      chart: {
        fontFamily: t.font,
        foreColor: t.axis,
        toolbar: { show: false },
        background: "transparent",
        height: height(),
      },
      labels: props.labels,
      colors: resolveColors(props.colors) ?? t.series,
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      legend: {
        show: true,
        position: "right",
        fontFamily: t.font,
        labels: { colors: t.axis },
        markers: { strokeWidth: 0 },
        itemMargin: { vertical: 3 },
      },
      tooltip: {
        theme: t.mode,
        style: { fontFamily: t.font },
        ...(props.valueFormatter ? { y: { formatter: props.valueFormatter } } : {}),
      },
      plotOptions: {
        pie: {
          donut: {
            size: isDonut() ? "68%" : "0%",
            labels: {
              show: isDonut(),
              name: { fontFamily: t.font, color: t.axis, fontSize: "12px" },
              value: {
                fontFamily: t.font,
                color: readVar("--c9-text", FALLBACK_TEXT),
                fontSize: "20px",
                fontWeight: 670,
              },
              total: {
                show: true,
                label: props.totalLabel ?? "Total",
                color: t.axis,
                formatter: (w) =>
                  `${w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)}`,
              },
            },
          },
        },
      },
      responsive: [{ breakpoint: 640, options: { legend: { position: "bottom" } } }],
    };
  });
  return (
    <Shell height={height()} class={props.class}>
      <ApexChart
        type={isDonut() ? "donut" : "pie"}
        width="100%"
        height={height()}
        options={options()}
        series={props.series}
      />
    </Shell>
  );
}

/** Single radial gauge (progress / utilisation). */
export function RadialGauge(props: {
  value: number;
  label?: string;
  height?: number;
  color?: string;
  class?: string;
}): JSX.Element {
  const theme = useChartTheme();
  const height = () => props.height ?? 260;
  const options = createMemo<ApexOptions>(() => {
    const t = theme();
    return {
      chart: {
        fontFamily: t.font,
        toolbar: { show: false },
        background: "transparent",
        height: height(),
      },
      colors: [props.color ? resolve(props.color) : t.series[0]!],
      labels: [props.label ?? ""],
      stroke: { lineCap: "round" },
      plotOptions: {
        radialBar: {
          hollow: { size: "62%" },
          track: { background: t.grid, strokeWidth: "100%" },
          dataLabels: {
            name: { fontFamily: t.font, color: t.axis, fontSize: "13px", offsetY: 22 },
            value: {
              fontFamily: t.font,
              color: readVar("--c9-text", FALLBACK_TEXT),
              fontSize: "28px",
              fontWeight: 670,
              offsetY: -16,
              formatter: (v) => `${Math.round(v)}%`,
            },
          },
        },
      },
    };
  });
  return (
    <Shell height={height()} class={props.class}>
      <ApexChart
        type="radialBar"
        width="100%"
        height={height()}
        options={options()}
        series={[props.value]}
      />
    </Shell>
  );
}
